/**
 * BarChart — pure-SVG vertical bar chart with animated rise + value labels.
 *
 * Bars grow from the baseline via CSS scaleY; each bar is staggered so the
 * chart "rolls in" left-to-right. Values fade in after the bar settles.
 */

import React, { useMemo } from 'react';

export interface BarDatum {
  key: string;
  label: string;
  value: number;
}

interface BarChartProps {
  data: BarDatum[];
  /** Optional formatter for the value rendered above each bar. */
  formatValue?: (n: number) => string;
  /** Color used for every bar. */
  color?: string;
  /** Highlights the highest bar with this color (subtle visual peak indicator). */
  peakColor?: string;
  /** Y-axis max — defaults to data peak with 15% headroom. */
  maxOverride?: number;
}

const VIEW_WIDTH  = 600;
const VIEW_HEIGHT = 220;
const PADDING_TOP    = 24;
const PADDING_BOTTOM = 28;
const PADDING_X      = 16;
const PLOT_HEIGHT = VIEW_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

export const BarChart: React.FC<BarChartProps> = ({
  data,
  formatValue = (n) => n.toLocaleString(),
  color = '#3b82f6',
  peakColor = '#1a1a2e',
  maxOverride,
}) => {
  const max = useMemo(() => {
    if (maxOverride !== undefined) return Math.max(maxOverride, 0.001);
    const peak = data.reduce((m, d) => Math.max(m, d.value), 0);
    return peak === 0 ? 1 : peak * 1.15; // headroom
  }, [data, maxOverride]);

  const peakValue = useMemo(
    () => data.reduce((m, d) => Math.max(m, d.value), 0),
    [data],
  );

  const plotWidth = VIEW_WIDTH - PADDING_X * 2;
  const slotWidth = data.length > 0 ? plotWidth / data.length : 0;
  const barWidth  = Math.max(4, slotWidth * 0.62);

  // 4 horizontal gridlines for visual scale
  const gridLines = [0.25, 0.5, 0.75, 1].map(t => PADDING_TOP + PLOT_HEIGHT * (1 - t));

  return (
    <div className="analytics-bar">
      <svg
        className="analytics-bar__svg"
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        role="img"
        preserveAspectRatio="none"
      >
        {gridLines.map((y, i) => (
          <line
            key={i}
            className="analytics-bar__gridline"
            x1={PADDING_X}
            x2={VIEW_WIDTH - PADDING_X}
            y1={y}
            y2={y}
          />
        ))}
        {/* Baseline */}
        <line
          x1={PADDING_X}
          x2={VIEW_WIDTH - PADDING_X}
          y1={PADDING_TOP + PLOT_HEIGHT}
          y2={PADDING_TOP + PLOT_HEIGHT}
          stroke="#d1d5db"
          strokeWidth={1}
        />

        {data.map((d, i) => {
          const slotX = PADDING_X + slotWidth * i;
          const cx = slotX + slotWidth / 2;
          const h = max > 0 ? (d.value / max) * PLOT_HEIGHT : 0;
          const y = PADDING_TOP + PLOT_HEIGHT - h;
          const isPeak = d.value > 0 && d.value === peakValue;
          return (
            <g key={d.key}>
              <rect
                className="analytics-bar__rect"
                x={cx - barWidth / 2}
                y={y}
                width={barWidth}
                height={Math.max(0, h)}
                rx={3}
                fill={isPeak ? peakColor : color}
                style={{ ['--bar-index' as string]: i }}
              />
              {d.value > 0 && (
                <text
                  className="analytics-bar__value"
                  x={cx}
                  y={y - 4}
                  style={{ ['--bar-index' as string]: i }}
                >
                  {formatValue(d.value)}
                </text>
              )}
              <text
                className="analytics-bar__label"
                x={cx}
                y={PADDING_TOP + PLOT_HEIGHT + 14}
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default BarChart;
